// Copyright 2026 Hardfin, Inc.
import { handleAPIError } from '@/api/error-utils';
import {
  HourglassEmptyOutlined,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { useSupportError } from '@/hooks/use-support-error';
import { useTimeout } from '@/hooks/use-timeout';
import { deleteMovements } from '@/movement/api/delete-movements';
import { Movement } from '@/movement/api/types';
import { movementsQueryKey } from '@/movement/hooks/use-movements';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// Estimated seconds per AEG delete (includes audit creation, cleanup)
const SECONDS_PER_DELETE = 0.15;

interface Props {
  isOpen: boolean;
  movements: Movement[];
  onClose: () => void;
  onSuccess: () => void;
}

export function DeleteMovementsDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval>>();
  const count = props.movements.length;

  const formSchema = useMemo(
    () =>
      z.object({
        count: z.number({ invalid_type_error: '' }).refine((v) => v === count),
      }),
    [count]
  );

  const { formState, reset, register, handleSubmit } = useForm<
    z.infer<typeof formSchema>
  >({
    resolver: zodResolver(formSchema),
  });

  // Reset form when the selection changes to prevent stale input
  useEffect(() => {
    reset();
  }, [count, reset]);

  const mutation = useMutation({
    mutationFn: () => deleteMovements(props.movements.map((m) => m.id)),
    onSuccess: () => {
      clearInterval(countdownRef.current);
      setCountdown(0);
      reset();
      queryClient.invalidateQueries({
        queryKey: movementsQueryKey,
      });
      props.onClose();
      props.onSuccess();
    },
    onError: (error: AxiosError) => {
      clearInterval(countdownRef.current);
      setCountdown(0);
      const apiError = handleAPIError(error);
      if (!apiError.sentToSentry) {
        showSupportError('Failed to delete movements');
      }
    },
  });

  const timeout = useTimeout(() => {
    reset();
  }, theme.transitions.duration.standard);

  // Clean up countdown on unmount
  useEffect(() => {
    return () => {
      clearInterval(countdownRef.current);
    };
  }, []);

  const estimatedSeconds = Math.ceil(count * SECONDS_PER_DELETE);

  const handleValidSubmission = () => {
    setCountdown(estimatedSeconds);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    mutation.mutate();
  };

  const close = () => {
    if (mutation.isPending) {
      return;
    }
    props.onClose();
    setCountdown(0);
    clearInterval(countdownRef.current);
    timeout.start();
  };

  const isDisabled = mutation.isPending || !formState.isValid;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={mutation.isPending ? undefined : close}
      disableEscapeKeyDown={mutation.isPending}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <DialogTitle>Delete movements</DialogTitle>
      <DialogContent dividers>
        <Typography marginBottom={2}>
          This will <b>permanently delete</b>{' '}
          {pluralize(count, { singular: 'movement' })} and all associated event
          data, such as tracking data.
        </Typography>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 300, marginBottom: 2 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Serial</TableCell>
                <TableCell>Origin</TableCell>
                <TableCell>Shipped</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Delivered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.movements.map((movement) => (
                <TableRow key={movement.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {movement.asset.serial}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {movement.originLocation?.name ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {movement.shippedAt
                        ? movement.shippedAt.toFormat('MMM d, yyyy')
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {movement.destinationLocation?.name ?? '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {movement.deliveredAt
                        ? movement.deliveredAt.toFormat('MMM d, yyyy')
                        : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert
          severity="warning"
          icon={<WarningAmberOutlined />}
          sx={{ marginBottom: 2 }}
        >
          <AlertTitle>This action cannot be undone</AlertTitle>
          Please review the above list to confirm only intended moves are in
          this list. Audit entries will be generated for this action.
        </Alert>

        {!mutation.isPending && (
          <Stack alignItems="flex-end">
            <TextField
              size="small"
              error={Boolean(formState.errors.count)}
              helperText="Type the number of movements you are planning to delete to confirm"
              InputProps={{
                ...register('count', {
                  setValueAs: (v: string) => {
                    if (v === '') return undefined;
                    const n = Number(v);
                    return Number.isNaN(n) ? undefined : n;
                  },
                }),
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              placeholder={String(count)}
              sx={{ width: '50%' }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ marginRight: 'auto', paddingLeft: 1 }}
        >
          <HourglassEmptyOutlined
            fontSize="small"
            sx={{
              color: 'text.secondary',
              ...(mutation.isPending
                ? {
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }
                : {}),
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {mutation.isPending && countdown > 0
              ? `Estimated time remaining: ${formatDuration(countdown)}`
              : mutation.isPending
                ? 'Almost finished...'
                : `Estimated time: ${formatDuration(estimatedSeconds)}`}
          </Typography>
        </Stack>
        <Button onClick={close} disabled={mutation.isPending}>
          No, cancel
        </Button>
        <Button
          type="submit"
          color="error"
          variant="contained"
          disabled={isDisabled}
        >
          {mutation.isPending ? 'Deleting...' : 'Yes, delete permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
